import React from 'react';
import { Grid as MuiGrid, GridProps as MuiGridProps } from '@mui/material';

/**
 * Grid Container Props
 */
export interface GridContainerProps extends Omit<MuiGridProps, 'container' | 'item'> {
  /** Gap between grid items (in px or MUI spacing units) */
  gap?: number | string;
  
  /** Row gap (overrides gap for rows) */
  rowGap?: number | string;
  
  /** Column gap (overrides gap for columns) */
  columnGap?: number | string;
  
  /** Children */
  children: React.ReactNode;
}

/**
 * Grid Item Props
 */
export interface GridItemProps extends Omit<MuiGridProps, 'container' | 'item'> {
  /** Number of columns to span (1-12) */
  xs?: number | 'auto' | boolean;
  sm?: number | 'auto' | boolean;
  md?: number | 'auto' | boolean;
  lg?: number | 'auto' | boolean;
  xl?: number | 'auto' | boolean;
  
  /** Children */
  children: React.ReactNode;
}

/**
 * GridContainer - Atomic grid layout component
 * 
 * Provides a consistent grid container abstraction over MUI Grid.
 * Use this in domain components instead of MUI Grid directly.
 * 
 * @example
 * ```tsx
 * <GridContainer gap={2}>
 *   <GridItem xs={8}>Main content (8/12 columns)</GridItem>
 *   <GridItem xs={4}>Sidebar (4/12 columns)</GridItem>
 * </GridContainer>
 * ```
 */
export function GridContainer({
  gap,
  rowGap,
  columnGap,
  children,
  sx,
  ...gridProps
}: GridContainerProps) {
  return (
    <MuiGrid
      container
      sx={{
        gap,
        rowGap,
        columnGap,
        ...sx,
      }}
      {...gridProps}
    >
      {children}
    </MuiGrid>
  );
}

/**
 * GridItem - Atomic grid item component
 * 
 * Provides a consistent grid item abstraction over MUI Grid.
 * Use this as children of GridContainer.
 * 
 * @example
 * ```tsx
 * <GridItem xs={12} md={6} lg={4}>
 *   Content that spans 12 cols on mobile, 6 on tablet, 4 on desktop
 * </GridItem>
 * ```
 */
export function GridItem({
  xs,
  sm,
  md,
  lg,
  xl,
  children,
  ...gridProps
}: GridItemProps) {
  return (
    <MuiGrid
      item
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      {...gridProps}
    >
      {children}
    </MuiGrid>
  );
}

export default GridContainer;
